<script lang="ts" setup>
import { useWindowScroll } from '@vueuse/core'
import { useUsersStore } from '~/stores/users'
import { useAuthStore } from '~/stores/auth'
import { useModal } from '~/composables/useModal'
import type BugReportForm from '~/components/organisms/forms/BugReportForm.vue'
import type { DarkModeType, ItemLink } from '~/types/elementsUi'
import type { RouteLocationRaw } from '#vue-router'
import type { NavbarItem } from '~/types/navbar'
import { darkModeIconMap } from '~/models/elementsUi'
import type { Locale } from '#shared/types/users.ts'

interface NavbarProps {
  showTransparentEffect?: boolean
}

const props = defineProps<NavbarProps>()

const route = useRoute()

const userStore = useUsersStore()
const localeStore = useLocaleStore()
const authStore = useAuthStore()

const { y } = useWindowScroll()

const {
  t,
  tm,
  locale,
  setLocale: innerSetLocale
} = useI18n('components.molecules.navbar.navbar')

const isActive = ref(false)
const isLoggingOut = ref(false)
const activeDropdown = ref<NavbarItem>()

const bugReportForm = useTemplateRef<InstanceType<typeof BugReportForm>>('bugReportForm')

const isBaseAccountPage = computed(() => route.name === 'account')

const setLocale = (locale: Locale) => {
  innerSetLocale(locale)
  localeStore.locale = locale

  if (activeDropdown.value === 'language') {
    activeDropdown.value = undefined
  }

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'clickEvents',
    params: {
      category: 'Klik in header menu',
      action: 'Taal-switch',
      label: locale
    }
  })
}

const { d_m, i_l } = useMagicKeys()
const darkMode = useColorMode()
whenever(d_m, () => {
  if (import.meta.env.DEV || isAdmin.value) {
    darkMode.preference = darkMode.value === 'dark' ? 'light' : 'dark'
  }
})

whenever(i_l, () => {
  if (import.meta.env.DEV || isAdmin.value) {
    setLocale(locale.value === 'nl' ? 'en' : 'nl')
  }
})

const setDarkModePreference = (value: 'dark' | 'light' | 'system') => {
  darkMode.preference = value

  if (activeDropdown.value === 'dark-mode') {
    activeDropdown.value = undefined
  }
}

const darkModeIcon = computed(() => darkModeIconMap[darkMode.preference as DarkModeType])

const {
  modalIsOpen: bugReportIsOpen,
  openModal: openBugReport,
  closeModal: closeBugReport
} = useModal()

const isLoggedInAs = computed(() => !!authStore.admin)
const isAdmin = computed(() => authStore.hasAdminRole)

const showHeroBackground = computed(() => {
  return isActive.value || !props.showTransparentEffect || y.value > 60
})

const aboutUsDropdownContent = computed<ItemLink[]>(() =>
  tm('.about-us-dropdown.dropdown-menu')
)
const helpOutDropdownContent = computed<ItemLink[]>(() =>
  tm('.help-out-dropdown.dropdown-menu')
)
const inspirationDropdownContent = computed<ItemLink[]>(() =>
  tm('.inspiration-dropdown.dropdown-menu')
)
const contactDropdownContent = computed<ItemLink[]>(() =>
  tm('.contact-dropdown.dropdown-menu')
)

const toggleHamburgerMenu = () => {
  isActive.value = !isActive.value
}

const forceCloseHamburgerMenu = () => {
  isActive.value = false
}

const logout = async () => {
  forceCloseHamburgerMenu()

  isLoggingOut.value = true

  await authStore.logout()
  await navigateTo({ name: 'index' })

  isLoggingOut.value = false
}

async function eventTracking(action: string) {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'clickEvents',
    params: {
      category: 'Klik in header menu',
      action: action,
      label: ''
    }
  })
}
</script>

<template>
  <div>
    <VHtmlButton
      v-if="isActive"
      class="fixed inset-0 z-10 backdrop-blur-xs 2xl:hidden"
      @click="isActive = false"
    />

    <nav
      class="navbar transition-[background-color] duration-200 ease-linear max-lg:overflow-y-auto 2xl:bg-accented"
      :class="[
        isActive &&
          'border-b max-2xl:max-h-[94vh] max-2xl:shadow-2xl dark:shadow-gray-800',
        showHeroBackground && 'bg-accented',
        isLoggedInAs && 'bg-danger!'
      ]"
      :aria-label="t('.aria-label')"
      role="navigation"
    >
      <div class="navbar-brand">
        <NavBarLogo />

        <VHtmlButton
          :class="[isActive && 'active', isLoggingOut && 'loading']"
          aria-expanded="false"
          aria-label="Menu"
          class="burger-menu"
          role="button"
          tabindex="0"
          @click="toggleHamburgerMenu"
        >
          <span class="sr-only">
            {{ isActive ? t('.hamburger.close') : t('.hamburger.open') }}
          </span>
          <span aria-hidden="true" />
          <span aria-hidden="true" />
          <span aria-hidden="true" />
        </VHtmlButton>
      </div>

      <div
        :class="[isActive && 'active', showHeroBackground && 'bg-accented']"
        class="navbar-menu bg-transparent transition-[background-color] duration-200 ease-linear"
      >
        <div class="navbar-start">
          <NavbarDropdown
            v-model:active="activeDropdown"
            content-key="about-us"
            title=".about-us-dropdown.dropdown-toggle"
          >
            <VLink
              v-for="option in aboutUsDropdownContent"
              :key="`about-us-dropdown-${option.link_DNT}-menu-item`"
              class="navbar-item no-underline"
              :to="{ name: option.link_DNT } as RouteLocationRaw"
              :label="option.item"
            />
          </NavbarDropdown>
          <NavbarDropdown
            v-model:active="activeDropdown"
            content-key="help-out"
            title=".help-out-dropdown.dropdown-toggle"
          >
            <VLink
              v-for="option in helpOutDropdownContent"
              :key="`help-out-dropdown-${option.link_DNT}-menu-item`"
              class="navbar-item no-underline"
              :to="
                option.hash_DNT
                  ? ({
                      name: option.link_DNT,
                      hash: `#${option.hash_DNT}`
                    } as RouteLocationRaw)
                  : ({ name: option.link_DNT } as RouteLocationRaw)
              "
              :label="option.item"
            />
          </NavbarDropdown>
          <NavbarDropdown
            v-model:active="activeDropdown"
            content-key="current"
            title=".inspiration-dropdown.dropdown-toggle"
          >
            <VLink
              v-for="option in inspirationDropdownContent"
              :key="`inspiration-dropdown-${option.link_DNT}-menu-item`"
              class="navbar-item no-underline"
              :to="{ name: option.link_DNT } as RouteLocationRaw"
              :label="option.item"
            />
          </NavbarDropdown>
          <NavbarDropdown
            v-model:active="activeDropdown"
            content-key="contact"
            title=".contact-dropdown.dropdown-toggle"
          >
            <VLink
              v-for="option in contactDropdownContent"
              :key="`contact-dropdown-${option.link_DNT}-menu-item`"
              class="navbar-item no-underline"
              :to="{ name: option.link_DNT } as RouteLocationRaw"
              :label="option.item"
            />
          </NavbarDropdown>
        </div>

        <div class="navbar-end">
          <NavbarDropdown
            v-model:active="activeDropdown"
            content-key="dark-mode"
            :sr-label="
              t('.dark-mode.sr-label', {
                activeValue: t(`.dark-mode.active-value.${darkMode.preference}`)
              })
            "
          >
            <template #title>
              <ClientOnly>
                <VIcon :icon="darkModeIcon" />
              </ClientOnly>
              <div class="ml-3 inline-flex flex-col">
                <p class="2xl:hidden" data-allow-mismatch>
                  {{ t(`.dark-mode.active-value.${darkMode.preference}`) }}
                </p>
              </div>
            </template>
            <VHtmlButton
              class="navbar-item flex items-center"
              @click="setDarkModePreference('system')"
            >
              <VIcon :icon="darkModeIconMap.system" />
              <span class="ml-3 inline-flex flex-col">
                <span>{{ t('.dark-mode.options.system.title') }}</span>
                <span class="text-sm text-muted">{{
                  t('.dark-mode.options.system.subtitle')
                }}</span>
              </span>
            </VHtmlButton>

            <VHtmlButton class="navbar-item" @click="setDarkModePreference('light')">
              <VIcon :icon="darkModeIconMap.light" />
              <span class="ml-3 inline-flex flex-col">
                {{ t('.dark-mode.options.light') }}
              </span>
            </VHtmlButton>

            <VHtmlButton class="navbar-item" @click="setDarkModePreference('dark')">
              <VIcon :icon="darkModeIconMap.dark" />
              <span class="ml-3 inline-flex flex-col">
                {{ t('.dark-mode.options.dark') }}
              </span>
            </VHtmlButton>
          </NavbarDropdown>

          <NavbarDropdown v-model:active="activeDropdown" content-key="language">
            <template #title>
              <div class="2xl:hidden">
                <VIcon :icon="locale === 'nl' ? 'fi-nl' : 'fi-gb'" icon-pack="fi" />
                <div class="ml-3 inline-flex flex-col">
                  {{ t(`.i18n.${locale}_DNT`) }}
                </div>
              </div>
              <p class="max-2xl:hidden">
                {{ locale.toLocaleUpperCase() }}
              </p>
            </template>

            <VHtmlButton
              class="navbar-item"
              :class="locale === 'nl' && 'max-2xl:hidden'"
              @click="setLocale('nl')"
            >
              <VIcon icon="fi-nl" icon-pack="fi" class="mr-1" />
              <span class="ml-3 inline-flex flex-col">
                {{ t('.i18n.nl_DNT') }}
              </span>
            </VHtmlButton>

            <VHtmlButton
              class="navbar-item"
              :class="locale === 'en' && 'max-2xl:hidden'"
              @click="setLocale('en')"
            >
              <VIcon icon="fi-gb" icon-pack="fi" class="mr-1" />
              <span class="ml-3 inline-flex flex-col">
                {{ t('.i18n.en_DNT') }}
              </span>
            </VHtmlButton>
          </NavbarDropdown>

          <div class="navbar-item px-0 max-2xl:ml-0 2xl:mr-4">
            <VButton
              :to="{ name: 'maaltijden' }"
              color="secondary"
              size="normal"
              width="full"
              class="text-base font-semibold shadow-none"
              label="buttons.foodie"
            />
          </div>

          <div v-if="!authStore.isAuthenticated" class="navbar-item px-0">
            <VButton
              :to="{ name: 'login' }"
              class="text-base font-semibold"
              color="transparent"
              outlined
              width="full"
              @click="eventTracking('Inloggen')"
            >
              {{ t('.account-dropdown.login') }}
            </VButton>
          </div>

          <div v-else-if="isBaseAccountPage" class="navbar-item px-0">
            <VButton
              class="text-base font-semibold"
              color="transparent"
              outlined
              width="full"
              @click="logout"
            >
              {{ t(`.account-dropdown.${authStore.admin ? 'admin-' : ''}logout`) }}
            </VButton>
          </div>

          <NavbarDropdown
            v-else
            v-slot="{ showOnMobile }"
            v-model:active="activeDropdown"
            content-key="account"
            title=".account-dropdown.account"
            right
            always-show-content-on-mobile
          >
            <template v-if="showOnMobile">
              <div class="flex flex-col">
                <VButton
                  :to="{ name: 'account' }"
                  class="navbar-item text-center"
                  width="full"
                  color="transparent"
                  outlined
                >
                  {{ t('.account-dropdown.account') }}
                </VButton>

                <VButton
                  v-if="isAdmin"
                  class="navbar-item text-center"
                  width="full"
                  color="transparent"
                  outlined
                  @click="openBugReport"
                >
                  {{ t('.account-dropdown.bug-report') }}
                </VButton>

                <VButton
                  class="navbar-item text-center"
                  width="full"
                  color="transparent"
                  outlined
                  @click="logout"
                >
                  {{ t(`.account-dropdown.${authStore.admin ? 'admin-' : ''}logout`) }}
                </VButton>
              </div>
            </template>
            <template v-else>
              <VLink :to="{ name: 'account' }" class="navbar-item no-underline">
                <VIcon class="mr-1" icon="fa-user-pen" />
                <span>
                  {{ t('.account-dropdown.account') }}
                </span>
              </VLink>

              <VHtmlButton v-if="isAdmin" class="navbar-item flex" @click="openBugReport">
                <VIcon icon="fa-bug" class="mr-1" />
                <span>
                  {{ t('.account-dropdown.bug-report') }}
                </span>
              </VHtmlButton>

              <VHtmlButton class="navbar-item flex" @click="logout">
                <VIcon class="mr-1" icon="fa-user" />
                <span>
                  {{ t(`.account-dropdown.${authStore.admin ? 'admin-' : ''}logout`) }}
                </span>
              </VHtmlButton>
            </template>
          </NavbarDropdown>
        </div>
      </div>
    </nav>

    <VModal
      v-if="isAdmin"
      v-model:open="bugReportIsOpen"
      title="components.organisms.forms.bug-report-form.title"
      description="components.organisms.forms.bug-report-form.description"
    >
      <template #content>
        <LazyBugReportForm ref="bugReportForm" @close="closeBugReport" />
      </template>
      <template #actions>
        <VButton
          type="submit"
          :form="bugReportForm?.formProps.id"
          :disabled="!bugReportForm?.meta.valid"
          :loading="bugReportForm?.isLoading"
          width="full"
          color="success"
        >
          {{ t('buttons.submit') }}
        </VButton>
      </template>
    </VModal>
  </div>
</template>

<style scoped></style>
